import React, { useState, useCallback } from 'react';
import AudioRecorder from './AudioRecorder';
import AIExampleSuggester from './AIExampleSuggester';
import { processAudioForSave, processExamplesForSave, uploadAudio, deleteAudio, getAudioUrl } from '../utils/audioHelpers';
import '../styles/admin.css';

/**
 * AdminPanel Component
 *
 * Admin interface for managing flashcard content:
 * - Add new words with hanzi, pinyin, meanings
 * - Add example sentences
 * - Upload images and audio files
 * - Preview cards before saving
 *
 * Matches the Chinese writing book aesthetic of the main app
 */
function AdminPanel({ onClose, onSave, initialData }) {
  const [formData, setFormData] = useState(initialData || {
    id: '',
    hanzi: '',
    pinyin: '',
    difficulty: 'beginner',
    hskLevel: 1,
    image: null,
    audio: null,
    topics: [],
    meanings: [{
      partOfSpeech: 'word',
      definition: '',
      examples: [{
        cn: '',
        en: '',
        pinyin: '',
        audio: null
      }]
    }],
    notes: ''
  });

  const [topicInput, setTopicInput] = useState('');
  // Tracks which meaning index has the AI suggestion panel open (or null).
  // We only allow one open at a time to keep the form focused.
  const [aiSuggestForMeaningIdx, setAiSuggestForMeaningIdx] = useState(null);

  const [imagePreview, setImagePreview] = useState(initialData?.image || null);
  const [audioFileName, setAudioFileName] = useState(initialData?.audio ? 'Existing audio' : '');
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [audioError, setAudioError] = useState('');

  // Check if we're editing an existing word (has ID from MongoDB)
  const isEditing = Boolean(initialData?.id);
  const wordId = initialData?.id;

  /**
   * Handle basic text input changes
   */
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  /**
   * Handle meaning field changes
   */
  const handleMeaningChange = useCallback((meaningIndex, field, value) => {
    setFormData(prev => {
      const newMeanings = [...prev.meanings];
      newMeanings[meaningIndex][field] = value;
      return {
        ...prev,
        meanings: newMeanings
      };
    });
  }, []);

  /**
   * Handle example sentence changes
   */
  const handleExampleChange = useCallback((meaningIndex, exampleIndex, field, value) => {
    setFormData(prev => {
      const newMeanings = [...prev.meanings];
      newMeanings[meaningIndex].examples[exampleIndex][field] = value;
      return {
        ...prev,
        meanings: newMeanings
      };
    });
  }, []);

  /**
   * Add new example sentence to a meaning
   */
  const addExample = useCallback((meaningIndex) => {
    setFormData(prev => {
      const newMeanings = [...prev.meanings];
      newMeanings[meaningIndex].examples.push({
        cn: '',
        en: '',
        pinyin: '',
        audio: null
      });
      return {
        ...prev,
        meanings: newMeanings
      };
    });
  }, []);

  /**
   * Append an AI-suggested example to a meaning's examples list. If the
   * first example slot is still empty (the default placeholder), fill it in
   * place rather than appending — keeps the form tidy.
   */
  const acceptAISuggestion = useCallback((meaningIndex, suggestion) => {
    const cleaned = {
      cn: suggestion.cn || '',
      pinyin: suggestion.pinyin || '',
      en: suggestion.en || '',
      audio: null,
    };
    setFormData(prev => {
      const newMeanings = [...prev.meanings];
      const examples = [...newMeanings[meaningIndex].examples];
      const firstEmpty = examples.findIndex(
        (e) => !e.cn && !e.pinyin && !e.en && !e.audio
      );
      if (firstEmpty !== -1) {
        examples[firstEmpty] = cleaned;
      } else {
        examples.push(cleaned);
      }
      newMeanings[meaningIndex] = { ...newMeanings[meaningIndex], examples };
      return { ...prev, meanings: newMeanings };
    });
  }, []);

  /**
   * Remove example sentence
   */
  const removeExample = useCallback((meaningIndex, exampleIndex) => {
    setFormData(prev => {
      const newMeanings = [...prev.meanings];
      newMeanings[meaningIndex].examples.splice(exampleIndex, 1);
      return {
        ...prev,
        meanings: newMeanings
      };
    });
  }, []);

  /**
   * Add new meaning
   */
  const addMeaning = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      meanings: [...prev.meanings, {
        partOfSpeech: 'word',
        definition: '',
        examples: [{
          cn: '',
          en: '',
          pinyin: '',
          audio: null
        }]
      }]
    }));
  }, []);

  /**
   * Remove meaning
   */
  const removeMeaning = useCallback((meaningIndex) => {
    setFormData(prev => {
      if (prev.meanings.length > 1) {
        const newMeanings = [...prev.meanings];
        newMeanings.splice(meaningIndex, 1);
        return {
          ...prev,
          meanings: newMeanings
        };
      }
      return prev;
    });
  }, []);

  /**
   * Add topic
   */
  const addTopic = useCallback(() => {
    const topic = topicInput.trim().toLowerCase();
    if (topic && !formData.topics.includes(topic)) {
      setFormData(prev => ({
        ...prev,
        topics: [...(prev.topics || []), topic]
      }));
      setTopicInput('');
    }
  }, [topicInput, formData.topics]);

  /**
   * Remove topic
   */
  const removeTopic = useCallback((topicToRemove) => {
    setFormData(prev => ({
      ...prev,
      topics: (prev.topics || []).filter(t => t !== topicToRemove)
    }));
  }, []);

  /**
   * Handle image upload
   */
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({
          ...prev,
          image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  /**
   * Handle audio upload
   */
  const handleAudioUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          audio: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  /**
   * Handle example audio upload
   */
  const handleExampleAudioUpload = useCallback((meaningIndex, exampleIndex, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => {
          const newMeanings = [...prev.meanings];
          newMeanings[meaningIndex].examples[exampleIndex].audio = reader.result;
          return {
            ...prev,
            meanings: newMeanings
          };
        });
      };
      reader.readAsDataURL(file);
    }
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      // Convert audio blobs to base64 for storage
      const audioBase64 = await processAudioForSave(formData.audio);
      const meaningsWithBase64 = await processExamplesForSave(formData.meanings);

      // Whitelist only fields the backend's CreateWordInput / UpdateWordInput
      // accepts. gqlgen rejects unknown fields with a 422 — and the form
      // carries form-only state like `id`, `notes`, `reviewStatus` that are
      // not part of the schema. Same goes for the nested meaning/example
      // shapes — keep them aligned with MeaningInput / ExampleInput.
      const cleanMeanings = (meaningsWithBase64 || []).map((m) => ({
        partOfSpeech: m.partOfSpeech,
        definition: m.definition,
        examples: (m.examples || []).map((ex) => ({
          cn: ex.cn,
          en: ex.en,
          pinyin: ex.pinyin,
          audio: ex.audio || null,
        })),
      }));

      const wordData = {
        hanzi: formData.hanzi,
        pinyin: formData.pinyin,
        difficulty: formData.difficulty,
        hskLevel: formData.hskLevel,
        topics: formData.topics || [],
        image: formData.image || null,
        audio: audioBase64,
        meanings: cleanMeanings,
      };

      onSave(wordData);
    } catch (error) {
      console.error('Error processing audio:', error);
      setSaveError('Failed to save audio. Please try again.');
      return;
    }

    // Only reset form if adding new (not editing)
    if (!initialData) {
      setFormData({
        id: '',
        hanzi: '',
        pinyin: '',
        difficulty: 'beginner',
        hskLevel: 1,
        image: null,
        audio: null,
        topics: [],
        meanings: [{
          partOfSpeech: 'word',
          definition: '',
          examples: [{
            cn: '',
            en: '',
            pinyin: '',
            audio: null
          }]
        }],
        notes: ''
      });
      setImagePreview(null);
      setAudioFileName('');
      setTopicInput('');
    }
  }, [formData, onSave, initialData]);

  return (
    <div className="admin-overlay">
      <div className="admin-panel">
        {/* Header */}
        <div className="admin-header">
          <h1 className="admin-title">{initialData ? 'Edit Word' : 'Add New Word'}</h1>
          <button className="admin-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Form */}
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-scroll-content">

            {/* Basic Info Section */}
            <section className="form-section">
              <h2 className="section-title">Basic Information</h2>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label className="form-label">Chinese Characters (汉字)</label>
                  <input
                    type="text"
                    name="hanzi"
                    className="form-input hanzi-input"
                    value={formData.hanzi}
                    onChange={handleInputChange}
                    placeholder="e.g., 你好"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label className="form-label">Pinyin</label>
                  <input
                    type="text"
                    name="pinyin"
                    className="form-input"
                    value={formData.pinyin}
                    onChange={handleInputChange}
                    placeholder="e.g., nǐ hǎo"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">ID (optional)</label>
                  <input
                    type="text"
                    name="id"
                    className="form-input"
                    value={formData.id}
                    onChange={handleInputChange}
                    placeholder="Auto-generated if empty"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Difficulty</label>
                  <select
                    name="difficulty"
                    className="form-select"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">HSK Level</label>
                  <select
                    name="hskLevel"
                    className="form-select"
                    value={formData.hskLevel}
                    onChange={handleInputChange}
                  >
                    {[1, 2, 3, 4, 5, 6].map(level => (
                      <option key={level} value={level}>HSK {level}</option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* Topics Section */}
            <section className="form-section">
              <h2 className="section-title">Topics / Categories</h2>
              <div className="topics-input-row">
                <input
                  type="text"
                  className="form-input"
                  value={topicInput}
                  onChange={(e) => setTopicInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
                  placeholder="e.g., food, travel, daily-routine"
                />
                <button
                  type="button"
                  className="btn-add-small"
                  onClick={addTopic}
                >
                  Add
                </button>
              </div>
              {formData.topics && formData.topics.length > 0 && (
                <div className="topics-list">
                  {formData.topics.map((topic, idx) => (
                    <span key={idx} className="topic-tag">
                      {topic}
                      <button
                        type="button"
                        className="topic-remove"
                        onClick={() => removeTopic(topic)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="form-hint">
                Add topics to categorize this word (e.g., food, travel, emotions, grammar)
              </p>
            </section>

            {/* Media Section */}
            <section className="form-section">
              <h2 className="section-title">Media</h2>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Image (optional)</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="file-input"
                    />
                    <label htmlFor="image-upload" className="file-upload-label">
                      {imagePreview ? '✓ Image Selected' : '📷 Choose Image'}
                    </label>
                  </div>
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Audio {!isEditing && '(save first to record)'}</label>
                  {isEditing ? (
                    <>
                      {/* Show existing audio or record new */}
                      {formData.audio ? (
                        <div className="audio-exists" style={{ marginBottom: '0.5rem' }}>
                          <button
                            type="button"
                            className="btn-audio-play"
                            onClick={() => {
                              const audio = new Audio(getAudioUrl(formData.audio));
                              audio.play();
                            }}
                          >
                            🔊 Play
                          </button>
                          <button
                            type="button"
                            className="btn-audio-delete"
                            disabled={isUploadingAudio}
                            onClick={async () => {
                              setIsUploadingAudio(true);
                              try {
                                await deleteAudio(wordId);
                                setFormData(prev => ({ ...prev, audio: null }));
                                setAudioFileName('');
                              } catch (err) {
                                setAudioError('Failed to delete: ' + err.message);
                              }
                              setIsUploadingAudio(false);
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      ) : null}
                      <AudioRecorder
                        onAudioRecorded={async (blob) => {
                          setIsUploadingAudio(true);
                          try {
                            const result = await uploadAudio(wordId, blob);
                            setFormData(prev => ({ ...prev, audio: result.url }));
                            setAudioFileName('Uploaded');
                          } catch (err) {
                            setAudioError('Upload failed: ' + err.message);
                          }
                          setIsUploadingAudio(false);
                        }}
                        onAudioDeleted={() => {}}
                        maxDuration={10}
                      />
                      {isUploadingAudio && <span style={{ fontSize: '0.8rem', color: 'var(--color-primary)' }}>Uploading...</span>}
                      {audioError && (
                        <p role="alert" style={{ color: 'var(--color-danger, #ef4444)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                          {audioError}
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={{ fontSize: '0.85rem', color: 'var(--color-ink-light)', fontStyle: 'italic' }}>
                      Save the word first, then edit to add audio recording.
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Meanings Section */}
            <section className="form-section">
              <div className="section-header">
                <h2 className="section-title">Meanings & Examples</h2>
                <button
                  type="button"
                  className="btn-add-small"
                  onClick={addMeaning}
                >
                  + Add Meaning
                </button>
              </div>

              {formData.meanings.map((meaning, meaningIndex) => (
                <div key={meaningIndex} className="meaning-card">
                  <div className="meaning-card-header">
                    <span className="meaning-number">Meaning {meaningIndex + 1}</span>
                    {formData.meanings.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeMeaning(meaningIndex)}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Part of Speech</label>
                      <select
                        className="form-select"
                        value={meaning.partOfSpeech}
                        onChange={(e) => handleMeaningChange(meaningIndex, 'partOfSpeech', e.target.value)}
                      >
                        <option value="noun">Noun</option>
                        <option value="verb">Verb</option>
                        <option value="adjective">Adjective</option>
                        <option value="adverb">Adverb</option>
                        <option value="phrase">Phrase</option>
                        <option value="word">Word</option>
                      </select>
                    </div>

                    <div className="form-group form-group-wide">
                      <label className="form-label">Definition</label>
                      <input
                        type="text"
                        className="form-input"
                        value={meaning.definition}
                        onChange={(e) => handleMeaningChange(meaningIndex, 'definition', e.target.value)}
                        placeholder="e.g., hello; hi"
                        required
                      />
                    </div>
                  </div>

                  {/* Examples */}
                  <div className="examples-section">
                    <div className="examples-header">
                      <h3 className="form-label">Example Sentences</h3>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="btn-add-tiny"
                          onClick={() =>
                            setAiSuggestForMeaningIdx(
                              aiSuggestForMeaningIdx === meaningIndex ? null : meaningIndex
                            )
                          }
                          disabled={!formData.hanzi.trim() || !meaning.definition.trim()}
                          title={
                            !formData.hanzi.trim() || !meaning.definition.trim()
                              ? 'Fill in hanzi and the definition first'
                              : 'Suggest examples with AI'
                          }
                        >
                          ✨ Suggest
                        </button>
                        <button
                          type="button"
                          className="btn-add-tiny"
                          onClick={() => addExample(meaningIndex)}
                        >
                          + Add Example
                        </button>
                      </div>
                    </div>

                    {aiSuggestForMeaningIdx === meaningIndex && (
                      <AIExampleSuggester
                        hanzi={formData.hanzi}
                        pinyin={formData.pinyin}
                        definition={meaning.definition}
                        partOfSpeech={meaning.partOfSpeech}
                        hskLevel={Number(formData.hskLevel) || 1}
                        onAccept={(ex) => acceptAISuggestion(meaningIndex, ex)}
                        onClose={() => setAiSuggestForMeaningIdx(null)}
                      />
                    )}

                    {meaning.examples.map((example, exampleIndex) => (
                      <div key={exampleIndex} className="example-card">
                        <div className="example-card-header">
                          <span className="example-number">Example {exampleIndex + 1}</span>
                          {meaning.examples.length > 1 && (
                            <button
                              type="button"
                              className="btn-remove-tiny"
                              onClick={() => removeExample(meaningIndex, exampleIndex)}
                            >
                              ×
                            </button>
                          )}
                        </div>

                        <div className="form-group form-group-full">
                          <label className="form-label-small">Chinese</label>
                          <input
                            type="text"
                            className="form-input form-input-small"
                            value={example.cn}
                            onChange={(e) => handleExampleChange(meaningIndex, exampleIndex, 'cn', e.target.value)}
                            placeholder="e.g., 你好！"
                          />
                        </div>

                        <div className="form-group form-group-full">
                          <label className="form-label-small">Pinyin</label>
                          <input
                            type="text"
                            className="form-input form-input-small"
                            value={example.pinyin}
                            onChange={(e) => handleExampleChange(meaningIndex, exampleIndex, 'pinyin', e.target.value)}
                            placeholder="e.g., nǐ hǎo!"
                          />
                        </div>

                        <div className="form-group form-group-full">
                          <label className="form-label-small">English</label>
                          <input
                            type="text"
                            className="form-input form-input-small"
                            value={example.en}
                            onChange={(e) => handleExampleChange(meaningIndex, exampleIndex, 'en', e.target.value)}
                            placeholder="e.g., Hello!"
                          />
                        </div>

                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>

            {/* Notes Section */}
            <section className="form-section">
              <h2 className="section-title">Notes (optional)</h2>
              <div className="form-group form-group-full">
                <textarea
                  name="notes"
                  className="form-textarea"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Add any additional notes or mnemonics..."
                  rows="3"
                />
              </div>
            </section>
          </div>

          {/* Footer Actions */}
          <div className="admin-footer">
            {saveError && (
              <p role="alert" style={{ color: 'var(--color-danger, #ef4444)', fontSize: '0.875rem', margin: '0 auto 0 0' }}>
                {saveError}
              </p>
            )}
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save Word
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminPanel;
